import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

import { PlusOutlined } from "@ant-design/icons";

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import Filter from "./components/environments/Filter";
import TableData from "./components/environments/TableData";
import BoxData from "./components/environments/BoxData";
import FormData from "./components/environments/FormData";

import {
  getRouterByLocation,
  scrollToTop,
  scrollEnd,
  parseDateToMs,
} from '../../../utils/common';

import { CipherType } from "../../../core-js/src/enums";

import coreServices from "../../../services/core";
import environmentServices from "../../../services/environment";
import commonServices from "../../../services/common";

import global from "../../../config/global";

import storeActions from "../../../store/actions";

import { socketSyncEnvironments } from "../../../utils/socket";

const ProjectEnvironments = (props) => {
  const { PageHeader } = commonComponents;
  const { NoData, Pagination, MultipleSelect } = itemsComponents;
  const { } = props;
  const { t } = useTranslation();
  const location = useLocation();

  const currentPage = getRouterByLocation(location);
  const projectId = currentPage?.params?.project_id;

  const isMobile = useSelector((state) => state.system.isMobile)
  const isReadOnly = useSelector((state) => state.project.isReadOnly)
  const locale = useSelector((state) => state.system.locale)
  const socketLastMessage = useSelector((state) => state.system.socketLastMessage)
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const projectEnvironments = useSelector((state) => state.project.projectEnvironments);
  const isFetchProjectEnvironments = useSelector((state) => state.project.isFetchProjectEnvironments);

  const syncAttemptedRef = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const hasFetchedWithHashRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [callingAPI, setCallingAPI] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [total, setTotal] = useState(0);
  const [environments, setEnvironments] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [params, setParams] = useState({
    page: 1,
    size: global.constants.PAGE_SIZE,
    name: '',
    orderField: 'updatedDate',
    orderDirection: 'desc'
  });

  const isProjectMatched = selectedProject && selectedProject.id == projectId;

  const filteredAndSortedEnvironments = useMemo(() => {
    if (!isProjectMatched) return [];
    let list = [...projectEnvironments];

    if (params.name) {
      const searchName = params.name.toLowerCase();
      list = list.filter((e) => e.name?.toLowerCase().includes(searchName));
    }

    const orderField = params.orderField || 'updatedDate';
    const orderDirection = params.orderDirection || 'desc';

    list.sort((a, b) => {
      if (orderField === 'name') {
        const valA = (a.name || '').toLowerCase();
        const valB = (b.name || '').toLowerCase();
        const comp = valA.localeCompare(valB);
        return orderDirection === 'desc' ? -comp : comp;
      }
      if (orderField === 'updatedDate' || orderField === 'revisionDate') {
        const timeA = parseDateToMs(a.updatedDate || a.updated_date || a.creationDate || a.creation_date);
        const timeB = parseDateToMs(b.updatedDate || b.updated_date || b.creationDate || b.creation_date);
        if (timeA !== timeB) {
          return orderDirection === 'desc' ? timeB - timeA : timeA - timeB;
        }
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return list;
  }, [isProjectMatched, projectEnvironments, params.name, params.orderField, params.orderDirection]);

  const currentPagingEnvironments = useMemo(() => {
    if (projectEnvironments.length > 0) {
      const startIndex = (params.page - 1) * params.size;
      return filteredAndSortedEnvironments.slice(startIndex, startIndex + params.size);
    }
    return [];
  }, [filteredAndSortedEnvironments, params.page, params.size, projectEnvironments.length]);

  const filterHash = useMemo(() => {
    if (projectEnvironments.length > 0) {
      const hashes = currentPagingEnvironments.map((e) => e.hash).filter(Boolean);
      return hashes.length > 0 ? hashes.join(',') : null;
    }
    return null;
  }, [currentPagingEnvironments, projectEnvironments.length]);

  useEffect(() => {
    hasFetchedWithHashRef.current = false;
    syncAttemptedRef.current = false;
    setEnvironments([]);
    setTotal(0);
  }, [projectId]);

  useEffect(() => {
    if (
      isProjectMatched &&
      selectedProject.total_environment > 0 &&
      projectEnvironments.length === 0 &&
      !isFetchProjectEnvironments &&
      !syncAttemptedRef.current
    ) {
      syncAttemptedRef.current = true;
      commonServices.sync_project_environments(selectedProject);
    }
  }, [isProjectMatched, selectedProject?.total_environment, projectEnvironments.length, isFetchProjectEnvironments]);

  useEffect(() => {
    fetchData();
  }, [projectId, JSON.stringify(params)]);

  useEffect(() => {
    if (isProjectMatched && !isFetchProjectEnvironments && projectEnvironments.length > 0) {
      fetchData();
    }
  }, [isProjectMatched, isFetchProjectEnvironments, projectEnvironments]);

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      name: currentPage?.query?.name,
    })
  }, [currentPage?.query?.name])

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      size: global.constants.PAGE_SIZE
    })
  }, [isMobile])

  useEffect(() => {
    if (socketLastMessage) {
      try {
        const strData = socketLastMessage?.data ? socketLastMessage.data.split("'").join('"') : null;
        if (!strData) return;
        const messageData = JSON.parse(strData);
        if (messageData.event === 'sync') {
          if (messageData.data?.project_id == projectId && messageData?.data?.id && messageData.type?.includes('env')) {
            socketSyncEnvironments(messageData);
          }
        }
      } catch (e) {
        console.error("Socket message parse error:", e);
      }
    }
  }, [socketLastMessage, projectId]);

  const handleChangePage = (page, size) => {
    scrollToTop();
    setParams({
      ...params,
      page,
      size
    })
  };

  const fetchData = async () => {
    if (isProjectMatched && selectedProject?.total_environment === 0) {
      setTotal(0);
      setEnvironments([]);
      setLoading(false);
      return;
    }

    if (projectEnvironments.length > 0 && filteredAndSortedEnvironments.length === 0) {
      setTotal(0);
      setEnvironments([]);
      setLoading(false);
      return;
    }

    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);

    try {
      let keyHash = filterHash;
      if (params.name && !keyHash && projectEnvironments.length === 0) {
        keyHash = await coreServices.hash_key_with_project_id(params.name, projectId);
      }
      const queryParams = {
        page: keyHash ? 1 : params.page,
        size: params.size,
        hash: keyHash
      };

      const response = await environmentServices.list(projectId, queryParams);
      if (requestId !== fetchRequestIdRef.current) return;

      const decryptedEnvs = await coreServices.decrypt_ciphers(response.results, CipherType.Environment);
      if (requestId !== fetchRequestIdRef.current) return;

      await updateHashCiphers(response.results, decryptedEnvs);
      if (requestId !== fetchRequestIdRef.current) return;

      let finalEnvs = decryptedEnvs;
      if (currentPagingEnvironments.length > 0) {
        finalEnvs = currentPagingEnvironments
          .map((item) => {
            const found = decryptedEnvs.find((e) => e.id == item.id);
            if (!found) return null;
            return {
              ...found,
              creationDate: item.creationDate || found.creationDate,
              updatedDate: item.updatedDate || found.updatedDate,
              revisionDate: item.revisionDate || found.revisionDate,
            };
          })
          .filter(Boolean);
      } else if (finalEnvs.length > 0) {
        finalEnvs.sort((a, b) => {
          if (params.orderField === 'name') {
            const valA = (a.environment?.name || a.name || '').toLowerCase();
            const valB = (b.environment?.name || b.name || '').toLowerCase();
            const comp = valA.localeCompare(valB);
            return params.orderDirection === 'desc' ? -comp : comp;
          }
          const timeA = parseDateToMs(a.updatedDate || a.updated_date || a.creationDate || a.creation_date);
          const timeB = parseDateToMs(b.updatedDate || b.updated_date || b.creationDate || b.creation_date);
          if (timeA !== timeB) {
            return params.orderDirection === 'desc' ? timeB - timeA : timeA - timeB;
          }
          const nameA = (a.environment?.name || a.name || '').toLowerCase();
          const nameB = (b.environment?.name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }

      if (projectEnvironments.length > 0) {
        setTotal(filteredAndSortedEnvironments.length);
      } else {
        setTotal(response.count);
      }
      setEnvironments(finalEnvs);
    } catch (err) {
      if (requestId === fetchRequestIdRef.current) {
        setTotal(0);
        setEnvironments([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const deleteItem = (environment) => {
    global.confirm(() => {
      environmentServices.remove(projectId, environment.id).then(async () => {
        global.pushSuccess(t('notification.success.environment.deleted'));
        global.store.dispatch(storeActions.updateIsFetchProjects(true));
        if (environments.length === 1 && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        } else {
          fetchData();
        }
      }).catch((error) => {
        global.pushError(error)
      });
    }, { content: t('environments.delete_question') });
  };

  const updateHashCiphers = async (items, ciphers) => {
    const noHashItems = items.filter((i) => !i.hash || i.hash.includes('=')).map((i) => ciphers.find((c) => c.id === i.id));
    if (noHashItems.length > 0) {
      const hashPayload = await environmentServices.hash_payload(noHashItems);
      await environmentServices.update_multiple(projectId, hashPayload);
    }
  }

  const deleteItems = () => {
    global.confirm(async () => {
      setCallingAPI(true)
      await environmentServices.remove_multiple(projectId, selectedRowKeys).then(async () => {
        global.pushSuccess(t('notification.success.environment.deleted'));
        if (environments.length === selectedRowKeys.length && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        } else {
          fetchData();
        }
        setSelectedRowKeys([])
      }).catch((error) => {
        global.pushError(error)
      });
      setCallingAPI(false)
    }, { content: t('environments.delete_question') });
  };

  const handleSelectionChange = (keys, key, value) => {
    if (keys) {
      const selectedEnvironments = environments.filter((env) => keys.includes(env.id))
      setSelectedRowKeys(selectedEnvironments.map((env) => env.id))
    } else {
      const selectedKeys = value ? [...selectedRowKeys, key] : selectedRowKeys.filter((k) => k !== key);
      setSelectedRowKeys(selectedKeys)
    }
  }

  return (
    <div
      className="environments layout-content"
      onScroll={(e) => scrollEnd(e, params, total, setParams)}
    >
      <PageHeader
        title={t('environments.title')}
        subtitle={t('environments.description')}
        docLabel={t('environments.doc')}
        total={total}
        docLink={global.urls.DOC_LINKS[locale].PROJECT_ENVIRONMENTS}
        actions={[
          {
            key: 'add',
            label: t('button.new_environment'),
            type: 'primary',
            icon: <PlusOutlined />,
            hide: isReadOnly,
            click: () => handleOpenForm()
          }
        ]}
      />
      {
        selectedProject?.total_environment > 0 && <div className="min-h-[55px] mt-6">
          {
            selectedRowKeys.length > 0 ? <MultipleSelect
              className={'pt-[13px]'}
              selectedRowKeys={selectedRowKeys}
              callingAPI={callingAPI}
              onDelete={deleteItems}
              onCancel={() => setSelectedRowKeys([])}
            /> : <Filter
              loading={loading}
              params={params}
              setParams={(v) => setParams({ ...v, page: 1 })}
            />
          }
        </div>
      }
      {
        total === 0 ? <NoData
          loading={loading}
          className={'mt-6'}
        /> : <>
          {
            isMobile ? <BoxData
              className="mt-4"
              loading={loading}
              data={environments}
              params={params}
              selectedRowKeys={selectedRowKeys}
              onUpdate={handleOpenForm}
              onDelete={deleteItem}
              selectionChange={handleSelectionChange}
            /> : <TableData
              className="mt-4"
              loading={loading}
              data={environments}
              params={params}
              selectedRowKeys={selectedRowKeys}
              onUpdate={handleOpenForm}
              onDelete={deleteItem}
              selectionChange={handleSelectionChange}
            />
          }
        </>
      }
      {
        total > global.constants.PAGE_SIZE && !isMobile && <Pagination
          params={params}
          total={total}
          onChange={handleChangePage}
        />
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        allItems={projectEnvironments.length > 0 ? projectEnvironments : environments}
        onClose={() => setFormVisible(false)}
        onReload={() => {
          if (!selectedItem) {
            if (params.page === 1) {
              fetchData();
            } else {
              setParams({
                ...params,
                page: 1,
              })
            }
          } else {
            fetchData();
          }
        }}
      />
    </div>
  );
}

export default ProjectEnvironments;